import { cn } from "@/lib/utils";
import { ChartTooltip, Typography } from "@backpac/monica";
import * as React from "react";
import { Area } from "recharts";
import type * as RechartsPrimitive from "recharts";
import type { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BaseChart } from "./base-chart";

type LinearChartProps = {
  data: { date: string; value: number }[];
  formatValue: (value: number) => string;
  dateType?: "month" | "day";
};

export function LinearChart(props: LinearChartProps) {
  const { data, formatValue, dateType = "day" } = props;
  const yAxisKey = "value";
  const highlightStrokeColor = "#0F0F0F";

  return (
    <BaseChart data={data} formatValue={formatValue} dateType={dateType}>
      <ChartTooltip
        cursor={{
          stroke: highlightStrokeColor,
          strokeDasharray: "2 2",
        }}
        content={
          <ChartTooltipContent
            formatLabel={(value: string) =>
              dateType === "month"
                ? new Date(value).toLocaleDateString("en-US", { month: "short", year: "numeric" })
                : new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            }
            formatValue={(value) => formatValue(value as number)}
          />
        }
      />
      <defs>
        <linearGradient id="black-gradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(15, 15, 15)" stopOpacity={0.2} />
          <stop offset="60%" stopColor="rgba(0, 0, 0)" stopOpacity={0} />
        </linearGradient>
      </defs>
      <Area
        type="linear"
        dataKey={yAxisKey}
        stroke={highlightStrokeColor}
        fill="url(#black-gradient)"
        activeDot={{
          r: 4,
          fill: "#ffffff",
          stroke: "#000000",
          strokeWidth: 1,
          filter: "drop-shadow(0px 0px 4px rgba(0, 0, 0, 1))",
        }}
      />
    </BaseChart>
  );
}

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      formatLabel?: (value: string) => string;
      formatValue?: (value: ValueType) => ValueType;
    }
>(({ active, payload, className, label, formatLabel = (x) => x, formatValue = (x) => x }, ref) => {
  if (!active || !payload?.length) {
    return null;
  }
  const item = payload[0];

  return (
    <div ref={ref} className={cn("rounded-sm bg-black px-2 py-2 opacity-80", className)}>
      <Typography size="xs" color="white">
        {formatLabel(label)}
      </Typography>
      <Typography size="xs" color="white" className="tabular-nums">
        {formatValue(item.value ?? 0)}
      </Typography>
    </div>
  );
});
